/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getClassStudents } from '../../../redux/sclassRelated/sclassHandle';
import { updateStudentFields } from '../../../redux/studentRelated/studentHandle';
import {
    Paper,
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CircularProgress,
    useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/system';
import { ThemeProvider, createTheme } from '@mui/material/styles';

const theme = createTheme({
    palette: {
        primary: {
            main: '#008080', // Teal
        },
        secondary: {
            main: '#FFD700', // Gold
        },
        action: {
            hover: '#E0FFFF', // Light Cyan
        },
    },
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:hover': {
        backgroundColor: theme?.palette?.action?.hover || '#E0FFFF',
    },
    '&.unmarked': {
        backgroundColor: '#FFEBEE',
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    margin: theme.spacing(0.5),
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    '&:hover': {
        transform: 'scale(1.08)',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
    },
}));

const StudentAttendance = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:600px)');

    const { sclassStudents, loading, error } = useSelector((state) => state.sclass);
    const { currentUser } = useSelector((state) => state.user);
    const { response, statestatus, error: attendanceError } = useSelector((state) => state.student);

    const classID = currentUser.teachSclass?._id;
    const subjectID = currentUser.teachSubject?._id;

    const [attendance, setAttendance] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState("");
    const [triedSubmit, setTriedSubmit] = useState(false);

    useEffect(() => {
        if (classID) {
            dispatch(getClassStudents(classID));
        }
    }, [dispatch, classID]);

    const handleAttendanceChange = (studentId, status) => {
        setAttendance(prev => ({ ...prev, [studentId]: status }));
        if (triedSubmit) setTriedSubmit(false);
    };

    const allStudentsMarked = () => {
        return sclassStudents.length > 0 && 
               sclassStudents.every(student => attendance[student._id]);
    };

    const handleSubmit = async () => {
        if (!allStudentsMarked()) {
            setTriedSubmit(true);
            setMessage("Please mark attendance for all students!");
            return;
        }

        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const attendanceData = sclassStudents.map(student => ({
                studentId: student._id,
                subName: subjectID,
                status: attendance[student._id],
                date: new Date().toISOString()
            }));

            const result = await dispatch(
                updateStudentFields(classID, attendanceData, "StudentAttendance")
            );

            if (result?.success) {
                setMessage("Attendance marked successfully");
                setAttendance({});
                setTriedSubmit(false);
            } else {
                setMessage(result?.error || "Failed to update attendance");
            }
        } catch (error) {
            setMessage("An unexpected error occurred");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ padding: isMobile ? 1 : 3, backgroundColor: '#F0FFF0', minHeight: '100vh' }}>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{ textAlign: 'center', color: '#008080', mb: 4, fontWeight: 'bold' }}>
                    Class Attendance Management
                </Typography>

                {loading ? (
                    <Box textAlign="center"><CircularProgress /></Box>
                ) : error ? (
                    <Typography color="error" textAlign="center">Error: {error}</Typography>
                ) : sclassStudents.length === 0 ? (
                    <Typography textAlign="center">No students in this class</Typography>
                ) : (
                    <Paper elevation={4} sx={{ p: isMobile ? 1 : 3, maxWidth: 800, mx: 'auto', borderRadius: 3 }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#FFD700' }}>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.875rem' : '1rem' }}>Student Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.875rem' : '1rem' }}>Roll Number</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: isMobile ? '0.875rem' : '1rem' }}>Attendance</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sclassStudents.map(student => (
                                    <StyledTableRow 
                                        key={student._id}
                                        className={!attendance[student._id] && triedSubmit ? 'unmarked' : ''}
                                    >
                                        <TableCell sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>{student.name}</TableCell>
                                        <TableCell sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>{student.rollNum}</TableCell>
                                        <TableCell align="center">
                                            <StyledButton
                                                variant={attendance[student._id] === 'present' ? 'contained' : 'outlined'}
                                                color="primary"
                                                onClick={() => handleAttendanceChange(student._id, 'present')}
                                                size={isMobile ? "small" : "medium"}
                                            >
                                                Present
                                            </StyledButton>
                                            <StyledButton
                                                variant={attendance[student._id] === 'absent' ? 'contained' : 'outlined'}
                                                color="secondary"
                                                onClick={() => handleAttendanceChange(student._id, 'absent')}
                                                size={isMobile ? "small" : "medium"}
                                            >
                                                Absent
                                            </StyledButton>
                                        </TableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Button
                                variant="contained"
                                size={isMobile ? "medium" : "large"}
                                onClick={handleSubmit}
                                disabled={!allStudentsMarked() || isSubmitting}
                                sx={{ bgcolor: '#008080', '&:hover': { bgcolor: '#006666' } }}
                            >
                                {isSubmitting ? <CircularProgress size={24} /> : 'Submit Attendance'}
                            </Button>
                            {message && (
                                <Typography sx={{ mt: 2, color: message.includes("success") ? 'primary.main' : 'error.main', fontSize: isMobile ? '0.875rem' : '1rem' }}>
                                    {message}
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                )}
            </Box>
        </ThemeProvider>
    );
};

export default StudentAttendance;